/**
 * @file     keywordMap.ts
 * @readme   ../CLAUDE.md
 * @purpose  Maps Google Ads utm_term keywords (often dynamically inserted) to inferred intent kind, location, property type, and persona.
 */

import type { DealFinderLocation, DealFinderPropertyType } from '@/core/types';
import type { IntentKind, IntentPersona } from './intentTypes';

export type KeywordSignal = {
  intent?: IntentKind;
  location?: DealFinderLocation;
  propertyType?: DealFinderPropertyType;
  persona?: IntentPersona;
};

const LOCATION_TOKENS: Array<{ tokens: string[]; location: DealFinderLocation }> = [
  { tokens: ['vancouver', 'yaletown', 'shaughnessy', 'olympic village', 'kitsilano', 'kits'], location: 'vancouver' },
  { tokens: ['burnaby', 'metrotown', 'brentwood'], location: 'burnaby' },
  { tokens: ['north vancouver', 'north van', 'north shore', 'west vancouver', 'west van'], location: 'north-shore' },
  { tokens: ['richmond'], location: 'richmond' },
  { tokens: ['surrey', 'langley', 'cloverdale', 'south surrey'], location: 'surrey-langley' },
];

const PROPERTY_TYPE_TOKENS: Array<{ tokens: string[]; propertyType: DealFinderPropertyType }> = [
  { tokens: ['condo', 'apartment'], propertyType: 'condo' },
  { tokens: ['townhouse', 'town home', 'townhome'], propertyType: 'townhouse' },
  { tokens: ['detached', 'house', 'homes ', 'home for sale', 'family home', 'luxury home'], propertyType: 'detached' },
  { tokens: ['investment', 'investor'], propertyType: 'investment-property' },
];

const INTENT_TOKENS: Array<{ tokens: string[]; intent: IntentKind }> = [
  { tokens: ['for sale', 'buy', 'buyer', 'home matching', 'shortlist', 'curated', 'home search'], intent: 'buyer' },
  { tokens: ['sell', 'home value', 'valuation', "what's my home worth", 'whats my home worth'], intent: 'seller' },
  { tokens: ['rent', 'rental', 'tenant', 'lease'], intent: 'renter' },
  { tokens: ['investment', 'cash flow', 'rental yield'], intent: 'investor' },
];

const PERSONA_TOKENS: Array<{ tokens: string[]; persona: IntentPersona }> = [
  { tokens: ['out of province', 'out-of-province', 'toronto buyers', 'remote home buying', 'remote buyer'], persona: 'out-of-province' },
  { tokens: ['investment', 'investor'], persona: 'investor' },
  { tokens: ['first time', 'first-time'], persona: 'first-time' },
  { tokens: ['family home'], persona: 'family' },
  { tokens: ['presale', 'pre-sale', 'pre construction', 'pre-construction'], persona: 'presale' },
];

const matchTokens = <T,>(haystack: string, table: Array<{ tokens: string[] } & T>): T | undefined => {
  for (const row of table) {
    if (row.tokens.some((tok) => haystack.includes(tok))) {
      const { tokens: _ignored, ...rest } = row;
      return rest as T;
    }
  }
  return undefined;
};

/**
 * Read a utm_term string (e.g. "homes for sale burnaby") and extract inferred signals.
 * Pure function. Lowercased haystack. Returns empty object if no matches.
 */
export const inferFromKeyword = (utmTerm: string | undefined): KeywordSignal => {
  if (!utmTerm) return {};
  const haystack = utmTerm.toLowerCase().replace(/[\[\]"+]/g, ' ').trim();
  if (!haystack) return {};

  const intentMatch = matchTokens<{ intent: IntentKind }>(haystack, INTENT_TOKENS);
  const locationMatch = matchTokens<{ location: DealFinderLocation }>(haystack, LOCATION_TOKENS);
  const propertyTypeMatch = matchTokens<{ propertyType: DealFinderPropertyType }>(haystack, PROPERTY_TYPE_TOKENS);
  const personaMatch = matchTokens<{ persona: IntentPersona }>(haystack, PERSONA_TOKENS);

  return {
    intent: intentMatch?.intent,
    location: locationMatch?.location,
    propertyType: propertyTypeMatch?.propertyType,
    persona: personaMatch?.persona,
  };
};
